import React, { useRef } from 'react';
import { FaPrint, FaArrowLeft } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from "react-helmet-async";
import './LifetimeMember.css';

const fmtDate = (d) => {
  if (!d) return ''; 
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) { 
    const [y, m, day] = d.split('-').map(Number); 
    return new Date(y, m - 1, day).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }); 
  } 
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' }); 
};

const getYear = (d) => {
  if (!d) return '';
  return new Date(d).getFullYear();
};

// Intelligent Address Splitter: Splits cleanly at a space near 55 chars
const splitAddress = (address) => {
  if (!address) return { line1: '', line2: '' };
  if (address.length <= 55) return { line1: address, line2: '' };
  
  let breakIdx = address.lastIndexOf(' ', 55);
  if (breakIdx === -1 || breakIdx < 30) breakIdx = 55; // Fallback split
  
  return {
    line1: address.substring(0, breakIdx).trim(),
    line2: address.substring(breakIdx).trim()
  };
};

const LifetimeMember = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { donation } = location.state || {}; 
  const formRef = useRef(null);

  // Split both addresses cleanly across 2 lines
  const permAddress = splitAddress(donation?.permanentAddress);
  const currAddress = splitAddress(donation?.currentAddress);

  const handlePrint = () => {
    if (!formRef.current) return;

    const printWindow = window.open('', '_blank', 'width=900,height=800');
    if (!printWindow) {
      alert("Please allow pop-ups to print the form.");
      return;
    }

    const printContent = formRef.current.innerHTML;
    let stylesHtml = '';
    for (const stylesheet of document.styleSheets) {
      try {
        let rules = '';
        for (const rule of stylesheet.cssRules) { rules += rule.cssText; }
        stylesHtml += `<style>${rules}</style>`;
      } catch (e) {
        if (stylesheet.href) { stylesHtml += `<link rel="stylesheet" href="${stylesheet.href}">`; }
      }
    }

    printWindow.document.write(`
      <html>
        <head>
          <title>Lifetime-Membership-Form_${donation?.memberId || 'Blank'}</title>
          ${stylesHtml}
          <style>
            @media print {
              @page { size: A4 portrait; margin: 0; }
              html, body { 
                margin: 0 !important; 
                padding: 0 !important; 
                width: 210mm !important; 
                height: 297mm !important; 
                overflow: hidden !important; 
                background: #fff !important; 
              }
              .lm-wrapper { margin: 0 !important; padding: 0 !important; }
              .lm-a4-container { 
                width: 210mm !important; 
                height: 297mm !important; 
                box-shadow: none !important; 
                margin: 0 !important; 
                padding: 10mm !important; 
                border: none !important; 
              }
              .lm-inner-border { height: 100% !important; box-sizing: border-box !important; }
              .lm-form-badge, .lm-bank-box, .lm-contact-box { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
              .lm-value-line { color: #000 !important; }
            }
          </style>
        </head>
        <body>
          <div style="width:100%; height:100%; margin:0; padding:0;">
            ${printContent}
          </div>
          <script>
            window.onload = function() { setTimeout(function() { window.print(); window.close(); }, 400); };
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  return (
    <>
    <Helmet>
                <title>Lifetime Membership Form | Rajput Shiksha Kosh</title>
            
                <meta
                  name="description"
                  content="View and print your lifetime membership form for the Rajput Shiksha Kosh organization."
                />
              </Helmet>
    <div className="lm-wrapper">
      <div className="lm-actions">
        <button className="lm-btn lm-btn-back" onClick={() => navigate(-1)}> <FaArrowLeft/> Back </button>
        <button className="lm-btn lm-btn-print" onClick={handlePrint}> <FaPrint/> Print Form </button>
      </div>

      <div ref={formRef} className="lm-a4-container">
        <div className="lm-inner-border">
          
          {/* Header Section */}
          <div className="lm-header">
            <h1 className="lm-title-big">राजपूत शिक्षा कोष</h1>
            <p className="lm-subtitle-small">द्वारा प्रायोजित</p>
            <h1 className="lm-title-big">राजपूत शिक्षा प्रोत्साहन निधि</h1>
            <p className="lm-address-small">कार्यालय मारवाड़ राजपूत सभा भवन, पावटा बी रोड, जोधपुर</p>
          </div>

          {/* Form Title Badge */}
          <div className="lm-badge-container">
            <div className="lm-form-badge">आजीवन सदस्यता प्रपत्र</div>
          </div>

          {/* Applicant Details Section */}
          <div className="lm-form-section">
            <div className="lm-row">
              <span className="lm-label">शुभ नाम</span>
              <div className="lm-value-line lm-value-left" style={{ flex: 1 }}>{donation?.name || ''}</div>
            </div>
            
            <div className="lm-row">
              <span className="lm-label">पिता का नाम श्री</span>
              <div className="lm-value-line lm-value-left" style={{ flex: 1 }}>{donation?.fatherName || ''}</div>
            </div>

            <div className="lm-row">
              <span className="lm-label">व्यवसाय</span>
              <div className="lm-value-line lm-value-left" style={{ flex: 1 }}>{donation?.occupation || ''}</div>
            </div>

            <div className="lm-row">
              <span className="lm-label">मो./ व्हाट्सएप नं.</span>
              <div className="lm-value-line" style={{ flex: 0.8 }}>{donation?.mobileNo || ''}</div>
              <span className="lm-label" style={{ marginLeft: '10px' }}>ई-मेल</span>
              <div className="lm-value-line lm-value-left" style={{ flex: 1.5 }}>{donation?.email || ''}</div>
            </div>

            <div className="lm-row">
              <span className="lm-label">आधार नं.</span>
              <div className="lm-value-line" style={{ flex: 1 }}>{donation?.aadharNo || ''}</div>
              <span className="lm-label" style={{ marginLeft: '10px' }}>पेन कार्ड नं.</span>
              <div className="lm-value-line" style={{ flex: 1 }}>{donation?.panNo || ''}</div>
            </div>

            {/* Permanent Address over 2 Lines */}
            <div className="lm-row">
              <span className="lm-label">स्थाई पता</span>
              <div className="lm-value-line lm-value-left" style={{ flex: 1 }}>{permAddress.line1}</div>
            </div>
            <div className="lm-row">
              <div className="lm-value-line lm-value-left" style={{ flex: 1 }}>{permAddress.line2}</div>
            </div>

            {/* Current Address over 2 Lines */}
            <div className="lm-row">
              <span className="lm-label">वर्तमान पता</span>
              <div className="lm-value-line lm-value-left" style={{ flex: 1 }}>{currAddress.line1}</div>
            </div>
            <div className="lm-row">
              <div className="lm-value-line lm-value-left" style={{ flex: 1 }}>{currAddress.line2}</div>
            </div>
          </div>

          {/* Declaration Section */}
          <div className="lm-declaration-section">
            <h3 className="lm-section-title">घोषणा पत्र</h3>
            <p className="lm-justify-text">
              मैं घोषणा करता / करती हूँ कि राजपूत शिक्षा कोष द्वारा प्रायोजित राजपूत शिक्षा प्रोत्साहन निधि की स्वेच्छा से आजीवन सदस्यता ग्रहण कर रहा / रही हूँ जिसके तहत न्यूनतम 5,000 रुपये प्रतिवर्ष आर्थिक शिक्षा सहयोग देता रहूंगा / देती रहूंगी।
            </p>
          </div>

          <hr className="lm-divider" />

          {/* Office Use Section */}
          <div className="lm-office-section">
            <h3 className="lm-section-title">कार्यालय उपयोग हेतु</h3>
            
            <div className="lm-row">
              <span className="lm-label">श्री</span>
              <div className="lm-value-line lm-value-left" style={{ flex: 1 }}>{donation?.name || ''}</div>
              <span className="lm-label" style={{ margin: '0 8px' }}>पुत्र / पत्नी श्री</span>
              <div className="lm-value-line lm-value-left" style={{ flex: 1 }}>{donation?.fatherName || ''}</div>
            </div>

            <div className="lm-row">
              <span className="lm-label">ने रसीद नं.</span>
              <div className="lm-value-line" style={{ flex: 1 }}>{donation?.receiptNo || ''}</div>
              <span className="lm-label" style={{ margin: '0 8px' }}>के तहत वर्ष</span>
              <div className="lm-value-line" style={{ flex: 1 }}>{getYear(donation?.date)}</div>
            </div>

            <div className="lm-row">
              <span className="lm-label">की स्थाई सदस्यता क्रम संख्या</span>
              {/* Inserted Member ID mapping here. Defaults to 'N/A' if empty */}
              <div className="lm-value-line" style={{ flex: 1 }}>{donation?.memberId || 'N/A'}</div>
              <span className="lm-label" style={{ marginLeft: '10px' }}>पर सदस्यता दी जाती है।</span>
            </div>
          </div>

          <hr className="lm-divider" />

          {/* Footer Note */}
          <div className="lm-note-section">
            <strong>नोट :-</strong> उक्त फॉर्म भर कर कार्यालय में व्यक्तिगत अथवा व्हाट्सएप के जरिए जमा करवा सकते है। सदस्यता शुल्क ₹5000 रुपये का भुगतान ऑनलाइन अथवा नगद जमा करा सकते हैं।
          </div>

          {/* Bottom Grid */}
          <div className="lm-bottom-grid">
            <div className="lm-bank-box">
              <strong className="lm-box-header">राजपूत शिक्षा कोष</strong>
              <div className="lm-box-text"><strong>बैंक का नाम -</strong> ICICI BANK &nbsp;&nbsp; <strong>शाखा -</strong> पावटा ‘C’ रोड, जोधपुर</div>
              <div className="lm-box-text"><strong>खाता संख्या -</strong> 68201701715 &nbsp;&nbsp; <strong>IFSC -</strong> ICIC0006828</div>
            </div>
            
            <div className="lm-contact-box">
              <strong className="lm-box-header">संपर्क सूत्र</strong>
              <div className="lm-box-text">94141-10437</div>
              <div className="lm-box-text">98292-66066</div>
            </div>
          </div>

          {/* Absolute Bottom Web Details */}
          <div className="lm-footer-web">
            Website - www.rajputshikshakosh.com &nbsp;|&nbsp; Email - rajputshikshakosh@gmail.com
          </div>

        </div>
      </div>
    </div>
    </>
  );
};

export default LifetimeMember;